import {
  filterOutNull,
  keys,
  makeObject,
  matchAll,
  memoizeFunction,
} from "@1js/functional";
import type { Placeholders } from "@1js/localization";
import { getPluralizedString } from "@1js/pluralization";
import * as React from "react";

export type Text<K extends string> = (placeholders: Placeholders<K>) => string;

export type StringResolverFunc<K extends string, T extends Text<K>> = (
  string: T,
  placeholders: Placeholders<K>
) => string;

export type JsxValues<K extends string> = {
  readonly [key in K]: JSX.Element | string;
};

type Template<K extends string> = ((
  jsxValues: JsxValues<K>
) => string | JSX.Element | null)[];
type Formatter<K extends string> = (
  jsxValues: JsxValues<K>
) => (JSX.Element | string)[];

/**
 * Format a string with placeholders into a JSX array. This function is designed to work with localized strings that are
 * declared via the declareStringWithPlaceholders function from @1js/localization.
 *
 * @param text A function to produce a formatted string given string values for the placeholders.
 * @param jsxValues An object with JSX elements to use as values for the placeholders.
 * @param count optional parameter for formatting strings with localized ranges.
 * @return An array of strings and JSX elements.
 */
export function formatJSX<K extends string, T extends Text<K>>(
  resolveString: StringResolverFunc<K, T>,
  text: T,
  jsxValues: JsxValues<K>,
  count?: number
): (JSX.Element | string)[] {
  const stringWithTokens = resolveStringWithTokens(
    resolveString,
    text,
    keys(jsxValues)
  );
  // The formatter is created only once per localized string and can be reused for different JSX values.
  const formatter = memoizedMakeFormatter(stringWithTokens, count);
  return formatter(jsxValues);
}

function resolveStringWithTokens<K extends string, T extends Text<K>>(
  resolveString: StringResolverFunc<K, T>,
  text: T,
  placeholderKeys: K[]
): string {
  const placeholderTokens = makeObject(
    placeholderKeyToFormatJsxKey,
    ...placeholderKeys
  );

  return resolveString(text, placeholderTokens);
}

/** Matches some leading text followed by a <<<placeholderKey>>> token. */
const tokenRegExp = /(.*?)<<<(.*?)>>>/g;

export const placeholderKeyToFormatJsxKey = (key: string) => `<<<${key}>>>`;

const makeFormatter = <K extends string>(
  rawStringWithTokens: string,
  count: number | undefined
): Formatter<K> => {
  // First find the correct pluralized version of a formatted string using provided count. Then use a regular expression to find
  // those tokens, and assemble a template array. Each element of the template array is a function that will return
  // either a string or a JSX element. Finally return a function that uses the template array to produce the desired
  // JSX array, given JSX values for the placeholders.
  const stringWithTokens = getPluralizedString(rawStringWithTokens, count);

  const matches = matchAll(stringWithTokens, tokenRegExp);
  if (!matches.length) {
    return () => [stringWithTokens].filter(Boolean);
  }

  const lastMatch = matches[matches.length - 1];
  const trailingText = stringWithTokens.substr(
    lastMatch.index + lastMatch[0].length
  );

  function flatten<T>(arr: T[][]): T[] {
    return arr.reduce((acc, val) => acc.concat(val), []);
  }

  const template: Template<K> = flatten(
    matches.map(([_, leadingText, placeholderKey], index) => [
      () => leadingText || null,
      (jsxValues: JsxValues<K>) => {
        const element: JSX.Element | string = jsxValues[placeholderKey as K];
        if (!element) {
          return null;
        } else if (typeof element === "string") {
          return element;
        } else {
          return React.cloneElement(element, { key: index });
        }
      },
    ])
  ).concat(() => trailingText || null);

  return (jsxValues: JsxValues<K>) =>
    filterOutNull(template.map((f) => f(jsxValues)));
};

const memoizedMakeFormatter =
  memoizeFunction<typeof makeFormatter>(makeFormatter);
